/**
 * Типы ответов API. Зеркало backend/schemas.py.
 *
 * Держатся руками сознательно: генератор из OpenAPI притащил бы сотни строк
 * шума ради пяти экранов, а расхождение поймает `npm run typecheck` в тот же
 * момент, когда сломается экран.
 */

export type Severity = 'info' | 'warning' | 'critical'

export interface Flag {
  code: string
  severity: Severity
  message: string
  exercise_id?: number | null
  muscle_group?: string | null
  data: Record<string, unknown>
}

export interface Exercise {
  id: number
  name: string
  pattern: string
  muscle_group: string
  equipment: string
  is_compound: boolean
  /** Движение завёл сам пользователь: его можно убрать, каталожное — нет. */
  is_custom?: boolean
  /** Убрано из выбора, но не из истории: подходы по нему остались. */
  is_archived?: boolean
  /**
   * Вес пустого грифа, кг. `null` — движение не штанговое.
   *
   * Это `min_weight` движения под своим именем: пустой гриф и есть минимально
   * возможный вес. Нужен экрану тренировки, чтобы поле веса спрашивало блины,
   * а не полную сумму.
   */
  bar_weight?: number | null
}

/** Своё движение. Шаг веса и минимальный вес выводит сервер из снаряда. */
export interface ExerciseInput {
  name: string
  muscle_group: string
  equipment: string
  is_compound?: boolean
  /** Вес пустого грифа. Осмыслен только для штанги, иначе сервер его не берёт. */
  bar_weight?: number | null
}

/**
 * Грифы на выбор. Три ходовых: женский олимпийский (10), короткий или
 * изогнутый (15) и обычный олимпийский (20). Это ассортимент стойки, а не
 * расчёт нагрузки: сервер принимает и другие веса, но кнопок на экране между
 * подходами должно быть ровно столько, сколько реально берут в руки.
 */
export const BAR_CHOICES = [10, 15, 20] as const

/** Гриф по умолчанию — обычный олимпийский. */
export const DEFAULT_BAR = 20

/** Мышечные группы — значения MuscleGroup из engine/types.py. */
export const MUSCLE_GROUPS = [
  'chest',
  'back',
  'shoulders',
  'biceps',
  'triceps',
  'forearms',
  'quads',
  'hamstrings',
  'glutes',
  'calves',
  'abs',
] as const

/** Снаряды — значения Equipment из engine/types.py. */
export const EQUIPMENT_KINDS = ['barbell', 'dumbbell', 'machine', 'cable', 'bodyweight'] as const

export interface PlannedSet {
  set_no: number
  target_weight: number | null
  target_reps_min: number
  target_reps_max: number
  target_rpe: number
  /** Подход на максимум повторений: в GZCLP и 5/3/1 он решает, двигать вес. */
  amrap: boolean
  /** Пометка от программы: «один рабочий подход», «объёмная часть». */
  note: string
}

export interface ExerciseBlock {
  exercise: Exercise
  sets: PlannedSet[]
  /** Вес уже посчитан движком и округлён под железо — клиент его не трогает. */
  suggested_weight: number | null
  reason: string
  last_session_weight: number | null
  last_session_reps: number | null
  /** Движение исключено из-за боли: подходов не будет (раздел 14). */
  has_active_pain: boolean
  /** Движение цели, под которую в блоке есть подходы. */
  goal_exercise_id?: number | null
  /** Сколько подходов блока добавлено под цель — посчитал сервер. */
  goal_sets?: number
  /** Весь блок добавлен под цель, а не часть подходов. */
  goal_whole_block?: boolean
}

export type SessionStatus = 'planned' | 'in_progress' | 'completed' | 'skipped'

/**
 * Куда применить правку тренировки: только в эту (`session`) или ещё и во все
 * оставшиеся тренировки того же дня до конца программы (`day`). Что считать
 * «тем же днём» и какие тренировки не трогать, решает сервер.
 */
export type EditScope = 'session' | 'day'

/** Ответ на отправку программы в чат с ботом. */
export interface ExportSent {
  sent: boolean
  filename: string
  /** Что сказать человеку — словами сервера. */
  message: string
}

export interface Session {
  id: number
  week_no: number
  day_no: number
  label: string
  status: SessionStatus
  planned_for_date: string | null
  is_deload: boolean
  is_calibration: boolean
  blocks: ExerciseBlock[]
  /**
   * Предупреждения движка о последней правке — например, что добавка вывела
   * группу за верх недельного коридора. Необязательное: ответы из кеша,
   * сохранённые до появления поля, его не несут.
   */
  flags?: Flag[]
}

/** Группа мышц из задачи дня и запас недельного коридора по ней. */
export interface DayGroup {
  muscle_group: string
  /** Название группы — показывается как есть. */
  name: string
  /** Проекция недели: факты плюс оставшийся план. */
  projected: number
  /** Целых подходов до верха коридора. `null` — у группы коридора нет. */
  room_sets: number | null
  in_session: boolean
}

/** Рекомендованное движение: только с причиной, словами движка. */
export interface SessionAddition {
  exercise: Exercise
  /** return_from_day | group_missing | below_corridor */
  kind: string
  muscle_group: string
  reason: string
}

/** Что добавить в тренировку под задачу её дня. */
export interface SessionAdditions {
  /** ok | deload | calibration | unknown_day */
  status: string
  message: string
  day_label: string
  source: string | null
  week_start: string | null
  groups: DayGroup[]
  items: SessionAddition[]
}

/** Что переехало при переносе тренировки на другую дату. */
export interface RescheduleResult {
  /**
   * Все затронутые тренировки с НОВЫМИ датами, от ранней к поздней.
   *
   * При сдвиге их десятки — клиент подставляет их в свой список, а не
   * перезапрашивает его и надеется на совпадение.
   */
  sessions: Session[]
  /** Сдвоенные даты, размер сдвига, дата в прошлом — показывать как есть. */
  flags: Flag[]
}

export interface RescheduleInput {
  planned_for_date: string
  /** Двигать вместе с ней и все следующие незакрытые тренировки. */
  shift_following: boolean
}

export interface Today {
  date: string
  session: Session | null
  message: string | null
  flags: Flag[]
  reason: string
}

export interface WorkoutSet {
  id: number
  exercise_id: number
  session_id: number | null
  set_no: number
  weight: number
  reps: number
  rpe: number | null
  is_warmup: boolean
  performed_at: string
  client_uuid: string | null
}

/** Тело запроса на запись подхода. `client_uuid` делает её идемпотентной. */
export interface SetInput {
  exercise_id: number
  set_no: number
  weight: number
  reps: number
  rpe?: number | null
  is_warmup?: boolean
  session_id?: number | null
  performed_at?: string
  client_uuid?: string
}

export interface Forecast {
  has_forecast: boolean
  target: number
  current_e1rm: number
  projected_date: string | null
  rate_kg_per_month: number | null
  r_squared: number
  n_points: number
  /** Честная причина, почему прогноза нет. Показывается как есть. */
  reason: string
}

export interface ProgressPoint {
  date: string
  e1rm: number
  top_weight: number
  top_reps: number | null
}

export interface Progress {
  exercise: Exercise
  points: ProgressPoint[]
  current_e1rm: number | null
  best_e1rm: number | null
  forecast: Forecast | null
  flags: Flag[]
}

/**
 * Успеваем ли к сроку по цели — вердикт движка.
 *
 * Отдельно от `forecast`, потому что отвечает на другой вопрос: прогноз
 * говорит «когда выйдешь», темп — «успеваешь ли к своему сроку». Сравнивать
 * даты самому клиенту нечем и незачем.
 */
export interface GoalPace {
  /** behind | on_track | not_moving | not_in_plan | achieved | unknown */
  status: string
  /** Показывается человеку как есть. */
  reason: string
  days_late: number | null
  projected_date: string | null
  target_date: string | null
  rate_kg_per_month: number | null
}

export interface Goal {
  id: number
  exercise_id: number
  target_1rm: number
  target_date: string | null
  status: string
  current_e1rm: number | null
  forecast: Forecast | null
  pace: GoalPace | null
}

/**
 * Что добавить в план под цель. Движение, день, подходы, место в дне, риски и
 * тексты решил движок — экран их только показывает.
 */
export interface GoalOffer {
  kind: 'goal_movement' | 'second_day' | 'helper' | 'more_sets'
  advice: 'recommended' | 'optional'
  place: 'first' | 'before_isolation' | 'end' | 'in_place'
  exercise: Exercise
  day_no: number
  day_label: string
  sets: number
  reps_min: number
  reps_max: number
  target_rpe: number
  percent_of_e1rm: number | null
  reason: string
  risks: string[]
  volume_after: Record<string, number>
  sessions_left: number
  first_date: string | null
}

export interface GoalAppliedItem {
  exercise: Exercise
  day_label: string
  sets_per_week: number
  /** goal_day — движение стоит в доп. занятии под цель. */
  source: 'manual' | 'rebuild' | 'goal_day'
}

export interface GoalApplied {
  items: GoalAppliedItem[]
  sessions_left: number
  message: string
  risks: string[]
  /**
   * Что ещё уйдёт из плана вместе с этой работой — словами сервера. Пусто или
   * нет поля (сервер прошлой версии) — уйдут только сами подходы.
   */
  removal_note?: string
}

/** Доп. упражнение под одну цель: предложение, уже добавленное или причина. */
export interface GoalAddition {
  goal_id: number
  goal_exercise_id: number
  goal_status: string
  state: 'offer' | 'applied' | 'none'
  message: string
  offer: GoalOffer | null
  applied: GoalApplied | null
}

export interface GoalAdditionResult {
  addition: GoalAddition
  sessions_changed: number
  flags: Flag[]
}

/**
 * Движение, по которому можно поставить цель, вместе с цифрами по нему.
 *
 * `suggested_target === null` — предлагать нечего, и `basis` объясняет почему.
 * Экран в этом случае отталкивается от текущего максимума и ждёт цифру от
 * человека: подставить правдоподобное число вместо расчёта нельзя.
 */
export interface GoalOption {
  exercise_id: number
  name: string
  muscle_group: string
  is_custom: boolean
  /** Вес тела (подтягивания, брусья): цель — это вес СВЕРХ собственного. */
  external_load_only: boolean
  current_e1rm: number | null
  suggested_target: number | null
  basis: string
  /** Пустой гриф, первая гантель, нижняя плитка стека — отправная точка поля. */
  min_weight: number
  has_open_goal: boolean
}

/** Тема экрана «Цели»: «Сильные руки», «Плечи», «Спина и подтягивания». */
export interface GoalTheme {
  code: string
  title: string
  hint: string
  options: GoalOption[]
}

export interface VolumeWeek {
  week_start: string
  volume: Record<string, number>
  flags: Flag[]
}

export interface VolumeReport {
  weeks: VolumeWeek[]
  target_min: number
  target_max: number
  /**
   * Группы, к которым коридор ОТНОСИТСЯ. Для остальных норматива нет, и
   * рисовать за их строкой зелёную зону 10–20 нельзя: движок сознательно не
   * выдаёт по ним вердикт, а картинка предъявила бы его за него.
   */
  corridor_groups: string[]
}

export interface BodyMetric {
  id: number
  measured_at: string
  weight_kg: number
  waist_cm: number | null
}

export interface RestTimer {
  fires_at: string
  seconds: number
  /**
   * `telegram` — придёт уведомление, `none` — не придёт: бот не подключён
   * или человек выключил сигнал на экране «Уведомления». Почему — в `note`.
   */
  delivery: 'telegram' | 'none'
  note: string
}

/** Что бот присылает в Telegram сам (`GET/PATCH /notifications`). */
export interface NotificationSettings {
  rest_timer: boolean
  daily_message: boolean
  /** «09:00» в поясе человека. */
  daily_message_time: string
  /** Бот подключён к сервису. Без него не придёт ничего, даже включённое. */
  bot_connected: boolean
}

export interface PainFlag {
  id: number
  exercise_id: number
  severity: number
  reported_at: string
  resolved_at: string | null
  note: string
}

export interface Mesocycle {
  id: number
  template_code: string
  mode: string
  weeks: number
  started_at: string
  status: string
  deload_weeks: number[]
  has_calibration_week: boolean
  sessions_total: number
  flags: Flag[]
  /** Имя плана для человека: «Фулбоди + доп. занятие под цель», «GZCLP». */
  title?: string
  /**
   * Готовая программа или план из шаблона движка. По виду кода их не
   * различить, а пересобирать надо по-разному — поэтому отвечает сервер.
   */
  kind?: 'program' | 'template'
  /** Шаблон движка без суффиксов: FB3 | UL4 | PPL5. `null` — план не из шаблона. */
  base_template?: string | null
  /** В плане стоит доп. занятие под цель. */
  goal_day?: boolean
}

export interface AuthResult {
  token: string
  user_id: number
  is_new: boolean
}


// --------------------------------------------------------------------------- //
// Готовые программы
// --------------------------------------------------------------------------- //

export interface ProgramSummary {
  code: string
  name: string
  author: string
  level: string
  goal: string
  days_per_week: number
  weeks: number
  weekly_sets: number
  summary: string
  /** Вес считается процентом от максимума: без истории движения весов не будет. */
  uses_percentages: boolean
}

export interface ProgramExercise {
  exercise: Exercise
  sets: number
  reps_min: number
  reps_max: number
  target_rpe: number
  percent_of_e1rm: number | null
  amrap: boolean
  note: string
}

export interface ProgramDay {
  label: string
  exercises: ProgramExercise[]
  total_sets: number
}

/** none | low | corridor | high | no_norm | untracked — решил движок. */
export type VolumeZone = 'none' | 'low' | 'corridor' | 'high' | 'no_norm' | 'untracked'

export interface MuscleContribution {
  exercise_id: number
  name: string
  sets: number
  weight: number
  contribution: number
  text: string
}

export interface MuscleGroupLoad {
  group: string
  name: string
  sets: number
  direct_sets: number
  secondary_sets: number
  /** «10.5 подх.» — строкой с сервера, клиент числа не форматирует. */
  sets_text: string
  zone: VolumeZone | string
  zone_label: string
  reason: string
  has_goal: boolean
  exercises: MuscleContribution[]
}

export interface MapWeek {
  week_no: number
  kind: 'regular' | 'calibration' | 'deload' | string
}

/** Нагрузка по мышцам за неделю плана — для схемы человека. */
export interface MuscleMap {
  status: 'ok' | 'calibration' | 'deload' | 'empty' | string
  source: 'plan' | 'program' | 'template' | string
  title: string
  message: string
  week_no: number | null
  week_kind: string | null
  weeks: MapWeek[]
  groups: MuscleGroupLoad[]
  excluded: { exercise_id: number; name: string; reason: string }[]
  legend: { zone: string; label: string }[]
  note: string
  headline: string
  summary: string
  target_min: number
  target_max: number
}

export interface ProgramDetail extends ProgramSummary {
  notes: string[]
  days: ProgramDay[]
  muscle_map?: MuscleMap | null
}

// --------------------------------------------------------------------------- //
// Программы движка
// --------------------------------------------------------------------------- //

/**
 * Можно ли к шаблону собрать доп. занятие под цель — и каким оно выйдет.
 *
 * Посчитано для этого человека: занятие собирается из его целей и из того,
 * что осталось до верха недельного коридора после дней шаблона.
 */
export interface GoalDayOption {
  available: boolean
  /** Итог сборки или причина отказа — словами движка, показывается как есть. */
  message: string
  exercises: number
  sets: number
  /** Неделя вместе с занятием — считает сервер, клиент чисел не складывает. */
  days_per_week: number
  weekly_sets: number
}

/** Программа движка в списке выбора. */
export interface TemplateSummary {
  /** FB3 | UL4 | PPL5 */
  code: string
  name: string
  summary: string
  days_per_week: number
  weekly_sets: number
  weeks: number
  goal_day: GoalDayOption
}

/**
 * Состав программы движка — такой, какой её соберёт пересборка. Свой у
 * каждого: движения подобраны под цели, зал и жалобы на боль.
 */
export interface TemplateDetail {
  code: string
  name: string
  summary: string
  days_per_week: number
  weekly_sets: number
  weeks: number
  /** Собралось ли доп. занятие. Просили, а его нет — причина во `flags`. */
  goal_day: boolean
  notes: string[]
  days: ProgramDay[]
  muscle_map?: MuscleMap | null
  flags: Flag[]
}

// --------------------------------------------------------------------------- //
// Правка состава тренировки
// --------------------------------------------------------------------------- //

export interface SessionExerciseInput {
  exercise_id: number
  sets?: number
  reps_min?: number
  reps_max?: number
  target_rpe?: number
}

/** Предложенная замена движения и причина, по которой она предложена. */
export interface ExerciseAlternative {
  exercise: Exercise
  /** «та же группа мышц» — показывается человеку как есть. */
  reason: string
  kind: string
}

export interface SessionExercisePatch {
  sets?: number
  reps_min?: number
  reps_max?: number
  target_rpe?: number
}

/**
 * Параметры пересборки мезоцикла. `program_code` задан — берём готовую
 * программу, `template_code` — программу движка. Всё, что не передано, сервер
 * наследует у текущего плана; пустое тело значит «пересобрать как было».
 */
export interface RebuildInput {
  program_code?: string | null
  /** Шаблон движка: FB3 | UL4 | PPL5. */
  template_code?: string
  days_per_week?: number
  weeks?: number
  mode?: string
  start_date?: string | null
  fat_loss_goal?: boolean
  /** Доп. занятие под цель сверх дней шаблона. */
  goal_day?: boolean
}
